import type { NextPage, GetStaticProps, GetStaticPaths } from 'next'
import Navbar from '../../components/layout/Navbar'
import Footer from '../../components/layout/Footer'
import { Fragment } from 'react'
import PageSeo from '../../components/seo/PageSeo'
import GameComponent from '../../components/game/Game'
import { buildGameSeo } from '../../utils/gameSeo'
import { serverFetch } from '../../utils/serverFetch'
import { fetchBannerAd } from '../../utils/homeData'
import { GUIDE_GAMES } from '../../utils/guideSeo'
import { isGuideBuilderKey } from '../../utils/guidePages'

const Game: NextPage = (props: any) => {
  const data = props.dataResponse
  const id = props.gameId
  return (
    <Fragment>
      <PageSeo seo={buildGameSeo({ gameId: id, game: data })} />
      <div className="flex flex-col min-h-screen bg-theme-bg text-theme-text ">
      <Navbar />
      <div className="flex-1 mt-20">
      {/* The page's single H1 is the game title in GameDetailCard (gamePage),
          now one responsive tree instead of separate desktop and mobile
          variants, so there is exactly one per document. */}
      { id && data ?
          <GameComponent gameId={id} title={data.title} reviewScore={data.reviewScore ? data.reviewScore : null} thumbnail={data.thumbnail} categories={data.categories ? data.categories : null} initialGame={data} initialBannerAd={props.bannerAd} initialSquareAd={props.squareAd} initialBottomAd={props.bottomAd} initialBottomSquareAd={props.bottomSquareAd} guideSlug={props.guideSlug ?? null} />
      : null }
      </div>
      <Footer />
      </div>
    </Fragment>
  )
}

export default Game

export const getStaticPaths: GetStaticPaths = async () => {
  // Public, id-keyed content — generate on demand and cache via ISR.
  return { paths: [], fallback: 'blocking' }
}

// The game's published Guide Builder guide, for the Guide button in
// GameDetailCard. The two hardcoded hubs are linked there by game id and skip
// the call. Uncached on purpose: publishing, unpublishing or deleting a guide
// revalidates /game/<gameId> from the admin API, and that render must read the
// new status, so the button appears or disappears right away. A failure only
// hides the button.
const HUB_GAME_IDS = new Set(Object.values(GUIDE_GAMES).map((g) => g.gameId))
const fetchGuideSlug = async (gameId: string): Promise<string | null> => {
  if (HUB_GAME_IDS.has(gameId)) return null
  try {
    const res = await serverFetch(`${process.env.BACKEND_BASE_URL}/guides/by-game/${gameId}`)
    const data = await res.json()
    const slug = data?.success ? data.data?.gameSlug : null
    return isGuideBuilderKey(slug) ? slug : null
  } catch {
    return null
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const gameId = context.params?.id as string
  // Garbage ids (robots.txt, .png, null) 404 without a backend round-trip. This
  // guard is also what makes the throw below safe: a non-ObjectId id makes the
  // backend answer 200 { success:false, data: undefined }, which would 500 here.
  if (!/^[0-9a-f]{24}$/i.test(gameId)) return { notFound: true, revalidate: 30 }
  try {
    // The two backend banner slots are seeded alongside the game so they render
    // on the server instead of shifting the page ~400 ms after hydration.
    const [res, bannerAd, squareAd, bottomAd, bottomSquareAd, guideSlug] = await Promise.all([
      serverFetch(`${process.env.BACKEND_BASE_URL}/games/${gameId}`),
      fetchBannerAd('GLOBAL', 'TOP'),
      fetchBannerAd('GLOBAL', 'TOP', 'SECOND'),
      fetchBannerAd('GLOBAL', 'BOTTOM'),
      fetchBannerAd('GLOBAL', 'BOTTOM', 'SECOND'),
      fetchGuideSlug(gameId),
    ])
    const data = await res.json()
    // /games/<missing> answers 200 with data.statusCode: 404 — only that (or a
    // real 404) is notFound; anything else throws so the stale ISR copy keeps
    // serving (OpenNext deletes the R2 key on notFound).
    if (res.status === 404 || data?.data?.statusCode === 404) {
      return { notFound: true, revalidate: 30 }
    }
    if (!res.ok || !data?.data) {
      throw new Error(`GET /games/${gameId} failed: HTTP ${res.status}`)
    }
    return {
      props: {
        dataResponse: data.data,
        gameId,
        bannerAd,
        squareAd,
        bottomAd,
        bottomSquareAd,
        guideSlug,
      },
      revalidate: 1800,
    }
  } catch (err) {
    // See above: a transient failure must not become a cached 404.
    throw err
  }
}
